// cmds/DisplayCmds.js
/**
 * @fileoverview
 * UserActions for altering the display to use import {UserActions} from 'BMapsCmds
 *
 * @typedef {import('BMapsSrc/style_manager').MolStyles} MolStyles
 * @typedef {import('BMapsModel').AtomGroup} AtomGroup
 * @typedef {import('BMapsModel').AtomGroupStyle} AtomGroupStyle
 * @typedef {import('BMapsModel').Atom} Atom
 *
 * @typedef DisplayCmds
 * @property {dumpWorkspace} DumpWorkspace
 * @property {stepBindingSiteRadius} StepBindingSiteRadius
 * @property {setBindingSiteRadius} SetBindingSiteRadius
 * @property {removeCase} RemoveCase
 * @property {getCameraPosition} GetCameraPosition
 * @property {restoreCameraPosition} RestoreCameraPosition
 * @property {rotateView} RotateView
 * @property {zoomView} ZoomView
 * @property {panView} PanView
 * @property {recenterView} RecenterView
 * @property {setView} SetView
 * @property {resetView} ResetView
 * @property {setDisplayStyle} SetDisplayStyle
 * @property {setSelectionColor} SetSelectionColor
 * @property {setAtomLabel} SetAtomLabel
 * @property {clearAtomLabels} ClearAtomLabels
 * @property {setHydrogenView} SetHydrogenView
 * @property {setWaterView} SetWaterView
 * @property {setHBonds} SetHBonds
 * @property {setBindingSiteSurface} SetBindingSiteSurface
 * @property {setLigandSolvation} SetLigandSolvation
 * @property {setHotspots} SetHotspots
 * @property {setFragmentEnergyFilter} SetFragmentEnergyFilter
 * @property {showBfactor} ShowBfactor
 * @property {setColorScheme} SetColorScheme
 * @property {activateCompound} ActivateCompound
 * @property {activateNextCompound} ActivateNextCompound
 * @property {toggleVisibility} ToggleVisibility
 * @property {toggleSelection} ToggleSelection
 * @property {toggleExpansion} ToggleExpansion
 * @property {moveTreeItem} MoveTreeItem
 * @property {groupCreate} GroupCreate
 * @property {groupItems} GroupItems
 * @property {groupCreate} GroupCreate
 * @property {groupRename} GroupRename
 * @property {groupUngroup} GroupUngroup
 * @property {groupDelete} GroupDelete
 * @property {groupSort} GroupSort
 * @property {changeTreeSort} ChangeTreeSort
 * @property {changeHotspotThreshold} ChangeHotspotThreshold
 * @property {setCustomAtomGroupStyle} SetCustomAtomGroupStyle
 * @property {removeCustomAtomGroupStyle} RemoveCustomAtomGroupStyle
 * @property {setCustomAtomsStyle} SetCustomAtomsStyle
 * @property {removeCustomAtomsStyle} RemoveCustomAtomsStyle
 * @property {setGlobalStyleState} SetGlobalStyleState
 * @property {redisplay} Redisplay
 * @property {redrawScene} RedrawScene
 * @property {toggleStarred} ToggleStarred
 * @property {setPiPiStacking} SetPiPiStacking
 * @property {setDipoles} SetDipoles
 */

import { StyleManager, DefaultMolStyleCode, MolStyles } from 'BMapsSrc/style_manager';
import { ColorChain, DefaultColorCode } from 'BMapsSrc/themes';
import { Polymer, canStyleAtomGroup, styleablePartition } from 'BMapsSrc/model/atomgroups';
import { DisplayStates, MolAtomGroupState, TreeMenuSorts } from 'BMapsSrc/model/DisplayState';
import { TreeItem } from 'BMapsSrc/model/TreeData';
import { Log } from 'BMapsSrc/Log';
import { ensureArray } from 'BMapsUtil/js_utils';
import { resolveSelection } from 'BMapsUtil/selection_state';
import { isCarbon } from 'BMapsUtil/chem_utils';
// Aliased on the way in: this module already has a local `redisplay` (registered as the
// Redisplay command) which publishes 'redisplayRequest'. The direct call is used below so the
// camera is painted synchronously with the write that changed it.
import {
    getCameraPosition as readCameraPosition,
    setCameraPosition as writeCameraPosition,
    redisplay as redrawCanvas,
    // Aliased for the same reason as the pair above: this module registers commands of these
    // names, and the local function is what the command points at.
    rotateView as rotateCamera,
    zoomView as zoomCamera,
    panView as panCamera,
    resetCameraOrientation,
} from 'BMapsSrc/MainCanvas';
import { pointsCentroid, isUsablePosition } from 'BMapsUtil/atom_distance_utils';
import { App } from '../BMapsApp';
import { EventBroker } from '../eventbroker';
import { UserActions, UserAction, UserCmd } from './UserCmd';
import {
    getColorScheme, setColorScheme as updateColorScheme, getBindingSiteDistance,
    setBindingSiteDistance,
} from '../redux/prefs/access';

/** @type {DisplayCmds} */
export const DisplayCmds = {
    DumpWorkspace: new UserCmd('DumpWorkspace', dumpWorkspace),
    StepBindingSiteRadius: new UserCmd('StepBindingSiteRadius', stepBindingSiteRadius, {
        capture: getBindingSiteDistance, restore: setBindingSiteDistance,
    }),
    SetBindingSiteRadius: new UserCmd('SetBindingSiteRadius', setBindingSiteRadius, {
        capture: getBindingSiteDistance, restore: setBindingSiteDistance,
    }),
    RemoveCase: new UserCmd('RemoveCase', removeCase),
    // noEffect: a pure read must not discard the redo branch. RestoreCameraPosition below is the
    // opposite case -- it changes the camera and has no undoFn, so it correctly invalidates redo.
    GetCameraPosition: new UserCmd('GetCameraPosition', getCameraPosition, { noEffect: true }),
    RestoreCameraPosition: new UserCmd('RestoreCameraPosition', restoreCameraPosition),
    // The camera movers: undo puts back the camera from before the command, so each command is
    // one undo step. A mouse drag is not a command, so undo after a drag also discards the drag.
    // After a view switch or reset, undo leaves the camera alone (see cameraUndo).
    RotateView: new UserCmd('RotateView', rotateView, cameraUndo()),
    ZoomView: new UserCmd('ZoomView', zoomView, cameraUndo()),
    PanView: new UserCmd('PanView', panView, cameraUndo()),
    RecenterView: new UserCmd('RecenterView', recenterView, cameraUndo()),
    // Not undoable yet: switching view means publication mode's saved view, which undo leaves to
    // the named-views design (docs/gibbs-migration-register.md, "Undo for named views").
    SetView: new UserCmd('SetView', setView),
    // Not undoable yet: a reset also resets the display state, so undo would have to capture that
    // as well as the camera.
    ResetView: new UserCmd('ResetView', resetView),
    // The appearance commands are undoable through capture / apply / restore (cmds/undoable.js):
    // capture snapshots what the call will touch before anything changes, and that snapshot is
    // what both undo and a failed command restore.
    SetDisplayStyle: new UserCmd('SetDisplayStyle', setDisplayStyle, {
        capture: captureDisplayStyle, restore: restoreAppearance,
    }),
    SetSelectionColor: new UserCmd('SetSelectionColor', setSelectionColor, {
        capture: captureSelectionColor, restore: restoreAppearance,
    }),
    SetAtomLabel: new UserCmd('SetAtomLabel', setAtomLabel, {
        capture: captureAtomLabel, restore: restoreAppearance,
    }),
    ClearAtomLabels: new UserCmd('ClearAtomLabels', clearAtomLabels, {
        capture: captureClearAtomLabels, restore: restoreAppearance,
    }),
    SetHydrogenView: new UserCmd('SetHydrogenView', setHydrogenView,
        displayStateUndo(DisplayStates.hydrogens)),
    SetWaterView: new UserCmd('SetWaterView', setWaterView,
        displayStateUndo(DisplayStates.waters)),
    SetHBonds: new UserCmd('SetHBonds', setHBonds, displayStateUndo(DisplayStates.hbonds)),
    SetPiPiStacking: new UserCmd('SetPiPiStacking', setPiPiStacking,
        displayStateUndo(DisplayStates.pipistacking)),
    SetDipoles: new UserCmd('SetDipoles', setDipoles, displayStateUndo(DisplayStates.dipoles)),
    SetBindingSiteSurface: new UserCmd('SetBindingSiteSurface', setBindingSiteSurface,
        displayStateUndo(DisplayStates.bindingSiteSurfaceState)),
    SetLigandSolvation: new UserCmd('SetLigandSolvation', setLigandSolvation,
        displayStateUndo(DisplayStates.functionalGroupHighlight)),
    SetHotspots: new UserCmd('SetHotspots', setHotspots,
        displayStateUndo(DisplayStates.hotspots)),
    SetFragmentEnergyFilter: new UserCmd('setFragmentEnergyFilter', setFragmentEnergyFilter),
    ShowBfactor: new UserCmd('ShowBfactor', showBfactor,
        displayStateUndo(DisplayStates.showBfactor)),

    SetColorScheme: new UserCmd('SetColorScheme', setColorScheme, {
        capture: getColorScheme, restore: updateColorScheme,
    }),
    ActivateCompound: new UserCmd('ActivateCompound', activateCompound, activeCompoundUndo()),
    ActivateNextCompound: new UserCmd('ActivateNextCompound', activateNextCompound,
        activeCompoundUndo()),
    ToggleVisibility: new UserCmd('ToggleVisibility', toggleVisibility,
        toggleUndo(MolAtomGroupState.Visible, toggleVisibility)),
    ToggleSelection: new UserCmd('ToggleSelection', toggleSelection,
        toggleUndo(MolAtomGroupState.Selected, toggleSelection)),
    ToggleExpansion: new UserCmd('ToggleExpansion', toggleExpansion,
        toggleUndo(MolAtomGroupState.Collapsed, toggleExpansion)),
    ToggleStarred: new UserCmd('ToggleStarred', toggleStarred,
        toggleUndo(MolAtomGroupState.Starred, toggleStarred)),

    MoveTreeItem: new UserCmd('MoveTreeItem', moveTreeItem),
    GroupCreate: new UserCmd('GroupCreate', groupCreate),
    GroupItems: new UserCmd('GroupItems', groupItems),
    GroupRename: new UserCmd('GroupRename', groupRename),
    GroupUngroup: new UserCmd('GroupUngroup', groupUngroup),
    GroupDelete: new UserCmd('GroupDelete', groupDelete),
    GroupSort: new UserCmd('GroupSort', groupSort),
    ChangeTreeSort: new UserCmd('ChangeTreeSort', changeTreeSort),

    ChangeHotspotThreshold: new UserCmd('ChangeHotspotThreshold', changeHotspotThreshold),
    SetCustomAtomGroupStyle: new UserCmd('SetCustomAtomGroupStyle', setCustomAtomGroupStyle, {
        capture: captureCustomAtomGroupStyle,
        restore: restoreAppearance,
        redoFn: redoCustomAtomGroupStyle,
    }),
    RemoveCustomAtomGroupStyle: new UserCmd('RemoveCustomAtomGroupStyle', removeCustomAtomGroupStyle),
    SetCustomAtomsStyle: new UserCmd('SetCustomAtomsStyle', setCustomAtomsStyle),
    RemoveCustomAtomsStyle: new UserCmd('RemoveCustomAtomssStyle', removeCustomAtomsStyle),
    SetGlobalStyleState: new UserCmd('SetGlobalStyleState', setGlobalStyleState, {
        capture: captureGlobalStyleState, restore: restoreAppearance,
    }),
    Redisplay: new UserCmd('Redisplay', redisplay),
    RedrawScene: new UserCmd('RedrawScene', redrawScene),
};

function dumpWorkspace() {
    console.log(App.Workspace.dumpState());
    UserAction.DumpQueues();
}

async function removeCase(caseDatas, { removeFromProjectTargets=true }={}) {
    const cases = ensureArray(caseDatas);
    // Announce the removal now, before the first await -- not when the server confirms it.
    // The server handles one command at a time, so a removal asked for during a minutes-long
    // command (a water-map derivation) is not confirmed until that command finishes, by which
    // point a loop over several structures has already sent the next one. This is the only
    // moment early enough for such a loop to skip a structure that is on its way out.
    for (const caseData of cases) EventBroker.publish('caseRemovalRequested', { caseData });
    for (const caseData of cases) {
        const { connector, caseDataCollection } = App.getDataParents(caseData);
        // caseDataMap includes "no protein" case, so 2 is the expected size with 1 loaded protein.
        if (caseDataCollection.caseDataMap.size === 2) {
            // If only 1 loaded protein, we can zap / remove connection
            await UserActions.RemoveConnection(connector);
        } else {
            // If more than one loaded protein, just remove the requested one
            await connector.cmdRemoveSolute(caseData.soluteName);
            App.Workspace.removeCase(caseData, { update: false, removeFromProjectTargets });
        }
    }
    // Refresh selectivity coloring for remaining molecules
    EventBroker.publish('atomGroupStateChanged');
    EventBroker.publish('refreshAtomDisplay');
    EventBroker.publish('redisplayRequest');
    EventBroker.publish('proteinsChanged');
}

/**
 * Step the binding-site radius by `delta` Angstrom, stopping at 0 (see setBindingSiteRadius).
 * Undo restores the radius captured before the step, so a step shortened by the floor is undone
 * exactly.
 * @param {number} delta
 */
function stepBindingSiteRadius(delta) {
    setBindingSiteRadius(getBindingSiteDistance() + delta);
}

/**
 * Set the binding-site radius to `radius` Angstrom, stopping at 0: a radius of 0 excludes the
 * whole protein, which is allowed; a negative one is not.
 * @param {number} radius
 */
function setBindingSiteRadius(radius) {
    setBindingSiteDistance(Math.max(0, radius));
}

// UserActions for updating view state
// Consider moving these to another file

/**
 * Read the current camera as an OPAQUE token, to be handed back to RestoreCameraPosition.
 *
 * The value is whatever the active visualizer uses to describe its camera, namespaced under a
 * key naming that visualizer -- '3DMol' today. It is deliberately not a neutral representation
 * and deliberately not documented field by field: treat it as a token, not a struct. Do not read
 * inside it, do not show it to a user, and do not compute with it. The same shape is what
 * SavedState stores as Display.Camera, so a token read here is the same thing a saved session
 * carries.
 *
 * To CHANGE the view, use the verbs -- SetView, ResetView -- not this.
 *
 * Read-only: renders nothing and changes no scene state. Each call builds a fresh object, so the
 * token does not alias anything live.
 *
 * @returns {object} opaque camera token
 */
function getCameraPosition() {
    return readCameraPosition();
}

/**
 * Put the camera back to a token previously returned by GetCameraPosition (or stored in a saved
 * session as Display.Camera).
 *
 * Absent token is a no-op rather than an error: pre-Camera saved sessions have none, so the guard
 * is a compatibility requirement, not defensiveness.
 *
 * Writes the camera and then paints it. The write alone does not redraw, and a caller that
 * forgets leaves the scene showing the old camera until something else happens to repaint.
 *
 * @param {object} [camera] opaque token from GetCameraPosition
 */
function restoreCameraPosition(camera) {
    if (!camera) return;
    writeCameraPosition(camera);
    redrawCanvas();
}

/**
 * The atoms a camera move should pivot about: everything displayed, minus water.
 *
 * Water is excluded because a solvated workspace is mostly water by count, so including it drags
 * the centroid away from the structure the user is looking at.
 */
function moleculeAtomsForPivot() {
    const workspace = App.Workspace;
    const waters = new Set(workspace.getAllWaterAtoms());
    // Active compounds are rendered even when their Compound Selector visibility bit is false.
    // getVisibleCompounds() is the canonical Workspace API for that rule. Union it with the
    // explicitly visible groups, because allAtomGroups().filter(isVisible) alone makes an
    // active-only ligand workspace look empty and refuses a rotation the user can plainly see.
    const displayedGroups = new Set([
        ...workspace.allAtomGroups().filter(workspace.isVisible),
        ...workspace.getVisibleCompounds(),
    ]);
    return [...new Set([...displayedGroups]
        .flatMap((atomGroup) => atomGroup.getAtoms())
        .filter((atom) => !waters.has(atom)))];
}

/**
 * Counts view switches (ligand / protein / publication) and resets, for cameraUndo. Touched by the
 * SetView and ResetView commands, which are the only ways the view changes outside a zap (and a
 * zap clears the undo history). A new command that changes the view must call touchViewChanges().
 */
let viewChanges = 0;

/** Record that the view changed, so camera undo steps from before it leave the camera alone. */
function touchViewChanges() {
    viewChanges += 1;
}

/**
 * Undo for a command that moves the camera: capture the camera before it moves, restore it on
 * undo. Restoring the saved camera is exact whatever happened in between; applying the inverse
 * move (-degrees, 1/factor) would not be once anything else had moved the camera.
 *
 * Except across a view switch or a reset, which are not undoable: the captured camera was framed
 * for the view before it, so undo leaves the camera where it is. Once switching view is undoable,
 * undo reverses the switch first and this check always passes; remove it and viewChanges then
 * (docs/gibbs-migration-register.md, "Undo for named views").
 */
function cameraUndo() {
    return {
        capture: () => ({ camera: getCameraPosition(), viewChanges }),
        restore: (snapshot) => {
            if (snapshot.viewChanges === viewChanges) {
                restoreCameraPosition(snapshot.camera);
            }
        },
    };
}

/** 'ligand', 'protein' or 'publication'. */
function currentView() {
    const controller = App.Workspace.displayStateController;
    if (controller.savedStateForPubMode) return 'publication';
    return controller.displayState.bindingsite ? 'ligand' : 'protein';
}

function rotateView(degrees, opts={}) {
    const atoms = moleculeAtomsForPivot();
    // Read each position once, then make the empty case explicit. pointsCentroid([]) returns
    // [0,0,0], which is ambiguous: it is also the legitimate centroid of a molecule centred at
    // the origin. Filtering first lets us refuse only when no real coordinate supports the pivot.
    const usablePositions = atoms
        .map((atom) => atom.getPosition())
        .filter(isUsablePosition);
    if (usablePositions.length === 0) {
        throw new Error(
            'Cannot rotate: no visible atoms with usable coordinates are loaded, '
            + 'so the molecule has no centre to turn about.'
        );
    }
    rotateCamera(degrees, opts.axis ?? 'y', pointsCentroid(usablePositions));
}

function zoomView(options={}) {
    // Accepts { factor } from the command interface's execute adapter, and a bare number from a
    // direct UserActions.ZoomView(2) call -- the natural programmatic form, and the shape
    // RotateView takes next door.
    //
    // ⚠️ The shape check is the point, and the else branch with it. Destructuring a primitive
    // yields undefined members, so the earlier `{ factor=2 }={}` signature replaced EVERY bare
    // number with the default: ZoomView(0.5) zoomed IN by 2 instead of out, silently. Value
    // validation stays downstream in viewTransform3Dmol.zoomView, which rejects anything
    // non-finite or <= 0 -- the bug was that a scalar never reached it.
    //
    // Defaulted here as well as in any ActionDef: CmdInterpreter pushes the parameter object only
    // when it is non-empty, so UserActions.ZoomView() with no argument is reachable. 2 is 3Dmol's
    // own default (GLViewer.zoom(factor = 2)).
    let factor;
    if (typeof options === 'number') {
        factor = options;
    } else if (options !== null && typeof options === 'object') {
        factor = options.factor ?? 2;
    } else {
        throw new TypeError(
            `Cannot zoom: expected a factor or { factor }, got: ${JSON.stringify(options)}`
        );
    }
    zoomCamera(factor);
}

function panView(options={}) {
    // Same shape hazard as zoomView above, but it surfaces differently: a bare number destructures
    // to three undefined components, all of which default to 0, so PanView(5) hit the zero-pan
    // guard below and reported "no direction was given" -- misleading, because a direction WAS
    // given, just not in a shape this accepts. A pan needs an axis, so a scalar is genuinely
    // ambiguous and still refused; it now says why.
    if (options === null || typeof options !== 'object') {
        throw new TypeError(
            `Cannot pan: expected { x, y, z } in Angstroms, got: ${JSON.stringify(options)}. `
            + 'A pan needs an axis, so a bare number is ambiguous.'
        );
    }
    const { x=0, y=0, z=0 } = options;
    // Refuse a zero displacement. Unlike "rotate 0 degrees" or "zoom by 1", which a caller has to
    // ask for explicitly, a zero pan is what an OMITTED direction produces -- all three components
    // default to 0 -- so it means the request never carried a direction at all.
    if (x === 0 && y === 0 && z === 0) {
        throw new Error(
            'Cannot pan: no direction or distance was given. '
            + 'Specify a shift along x, y or z in Angstroms.'
        );
    }
    panCamera([x, y, z]);
}

/**
 * Re-frame on what is displayed, through the same seam as the UI's Recenter control.
 *
 * Framing only: deliberately does NOT reset the camera orientation, which is ResetView's job.
 * Keeping them apart leaves one camera-only framing command, where ResetView is broad --
 * displayStateController.reset() clears all display styling too.
 *
 * Whether the Recenter button should reorient as well is an open product question. If it should,
 * the change is contained: this command's only caller is ui/RightControlPanel/utils.js. The
 * internal reframing paths -- protein load, session restore, active compound change -- call
 * redrawScene({ recenter: true }) directly and do not come through here.
 */
function recenterView() {
    redrawScene({ recenter: true });
}

function setView(view) {
    const before = currentView();
    App.Workspace.displayStateController.setView(view);
    if (currentView() !== before) touchViewChanges();
}

/**
 * Put the view back to defaults -- display state AND camera orientation.
 *
 * The orientation half is easy to miss: zoomTo() recomputes modelGroup.position and
 * rotationGroup.position.z but never touches the quaternion, so nothing in the redraw path
 * restores the angle. Without the explicit reset, "reset view" left the structure at whatever
 * angle it was last dragged to, which is not what the control advertises.
 *
 * Orientation first, so the redraw that follows paints the structure as it will be seen.
 *
 * Both callers are user-initiated: the Reset control and the `?view=reset` URL parameter.
 * RecenterView is framing-only by choice; see its note above.
 */
function resetView() {
    touchViewChanges();
    resetCameraOrientation();
    App.Workspace.displayStateController.reset();
    // Right now a change in protein style only won't reset the display,
    // so need to force it here.
    EventBroker.publish('redrawSceneRequest');
}

// ─── Appearance commands ──────────────────────────────────────────────────────────────────
// Each is registered with capture / apply / restore (cmds/undoable.js). The command function is
// the command, with its callers' arguments. Its `capture*` twin works out what the same call
// would touch and snapshots exactly that style state, before anything changes; a small shared
// helper per command (e.g. displayStyleTarget) keeps the two from disagreeing. `restore` is
// restoreAppearance for all of them: it is what undo applies, and what a command that threw
// half-way is rolled back with. Nothing here keeps undo state; that is the seam's job.

export const SelectionAppearanceStyles = Object.freeze([
    MolStyles.default,
    MolStyles.hidden,
    MolStyles.invisible,
    MolStyles.wireframe,
    MolStyles.sticks,
    MolStyles.ballandstick,
    MolStyles.spacefill,
    MolStyles.cartoon,
    MolStyles.tribbons,
    MolStyles.surface,
]);

function unique(items) { return [...new Set(items)]; }

// Which group types can be styled is decided in one place: StyleableAtomGroupTypes, in
// model/atomgroups (canStyleAtomGroup, styleablePartition).
function supportedAppearanceGroups() {
    if (!App.Workspace) throw new Error('Cannot change appearance: no workspace is loaded.');
    return App.Workspace.allAtomGroups().filter(canStyleAtomGroup);
}

function supportedAppearanceAtoms() {
    return unique(supportedAppearanceGroups()
        .flatMap((atomGroup) => atomGroup.getSelectableAtoms()));
}

/**
 * @param {string|{query: string}|((atom: Atom) => boolean)} selection - a serializable
 *     select-atoms query (see util/selection_state), or a predicate for the older UI seam
 */
function resolveAppearanceTarget(selection) {
    const selectorFn = typeof selection === 'function' ? selection : resolveSelection(selection);
    const atoms = supportedAppearanceAtoms().filter(selectorFn);
    if (atoms.length === 0) {
        // Say which: nothing matched, or only groups that cannot be styled (waters) matched.
        const unstyleableTypes = unique(App.Workspace.allAtomGroups()
            .filter((group) => !canStyleAtomGroup(group)
                && group.getSelectableAtoms().some(selectorFn))
            .map(({ type }) => type));
        if (unstyleableTypes.length > 0) {
            throw new TypeError(
                `Styling ${unstyleableTypes.join(', ')} groups is not supported yet.`
            );
        }
        throw new Error('selection matched no loaded atoms');
    }
    return { selectorFn, atoms };
}

function visibleProteinAtoms() {
    const workspace = App.Workspace;
    let components = workspace.getProteinChains().filter(workspace.isVisible);
    const bindingSite = workspace.displayStateController.displayState.bindingsite
        && workspace.getBindingSite();
    if (bindingSite) {
        components = components.flatMap((chain) => chain.getResidues());
        components = bindingSite.getComponentsInRange(components);
    }
    return unique(components.flatMap((component) => component.getSelectableAtoms()));
}

function sameMembers(left, right) {
    if (left.length !== right.length) return false;
    const rightSet = new Set(right);
    return left.every((item) => rightSet.has(item));
}

/** The groups a style change over `atoms` applies to; groups that cannot be styled are skipped. */
function partitionAppearanceAtoms(atoms) {
    return styleablePartition(App.Workspace.partitionAtomsIntoGroups(atoms));
}

function validateAppearanceStyle(style, atoms, { explicitSelection=false }={}) {
    if (typeof style !== 'string' || !SelectionAppearanceStyles.includes(style)) {
        throw new TypeError(`Unsupported molecular style '${style}'.`);
    }
    if (!atoms) return;

    const { primaryGroups, extraAtomsMap } = partitionAppearanceAtoms(atoms);
    if ([MolStyles.cartoon, MolStyles.tribbons].includes(style)
        && (primaryGroups.length === 0
            || primaryGroups.some((group) => !(group instanceof Polymer))
            || [...extraAtomsMap.values()].some((extraAtoms) => extraAtoms.length > 0))) {
        throw new TypeError(`${style} requires a selection of complete Polymer groups.`);
    }
    if (style === MolStyles.surface
        && (!explicitSelection || !sameMembers(atoms, visibleProteinAtoms()))) {
        throw new TypeError(
            'surface requires the selection to match the complete visible protein exactly.'
        );
    }
}

/**
 * Every group and atom a style change over `atoms` can touch: what its snapshot must cover.
 * Groups that cannot be styled are skipped here exactly as the change itself skips them (a
 * toolbar selection that includes a water still styles the rest).
 */
function mutationScope(atoms) {
    const partition = partitionAppearanceAtoms(atoms);
    const groups = unique([...partition.primaryGroups, ...partition.subsumedGroups]);
    const scopedAtoms = unique([
        ...[...partition.extraAtomsMap.values()].flat(),
        ...partition.primaryGroups.flatMap((group) => group.getAtoms()),
    ]);
    return { ...partition, groups, atoms: scopedAtoms };
}

/**
 * The style state a change can touch: per-group custom styles, per-atom style and color, and
 * (when asked) the global style state. Exactly what restoreAppearance puts back.
 */
function captureAppearance({ groups=[], atoms=[], global=false }={}) {
    return {
        groups: unique(groups).map((group) => [
            group, { ...StyleManager.getCustomAtomGroupStyle(group) },
        ]),
        atoms: unique(atoms).map((atom) => [atom, {
            molStyle: StyleManager.getCustomAtomStyle(atom),
            color: StyleManager.getCustomAtomColor(atom),
        }]),
        global: global ? StyleManager.getGlobalStyleState() : null,
    };
}

/**
 * Put a captured appearance back and repaint. The restore for all six commands, on undo and on
 * rollback alike: a rollback happens because an exception is propagating, and that exception
 * skips whatever repaint the caller would have done next.
 */
function restoreAppearance(snapshot) {
    if (!snapshot) return;
    snapshot.groups.forEach(([group, style]) => {
        StyleManager.replaceCustomAtomGroupStyle(group, style);
    });
    snapshot.atoms.forEach(([atom, values]) => {
        if (values.molStyle === undefined) StyleManager.removeCustomAtomsStyle([atom]);
        else StyleManager.setCustomAtomsStyle([atom], values.molStyle);
        if (values.color === undefined) StyleManager.removeCustomAtomsColor([atom]);
        else StyleManager.setCustomAtomsColor([atom], values.color);
    });
    if (snapshot.global) StyleManager.setGlobalStyleState(snapshot.global);
    requestAppearanceRefresh();
}

function requestAppearanceRefresh() {
    EventBroker.publish('refreshAtomDisplay');
    EventBroker.publish('redisplayRequest');
}

/**
 * SetDisplayStyle(style, selection?)
 * With a selection: a serializable select-atoms query (or a predicate, for the older UI seam),
 * validated against the style. Without one: the current UI selection, or the whole protein when
 * nothing is selected -- the legacy one-argument toolbar command, whose implicit selection is not
 * the serializable API contract, so the API's group-scope rules do not make it start throwing.
 */
function setDisplayStyle(style, selection) {
    const { selectorFn, atoms, globalTarget } = displayStyleTarget(style, selection);
    EventBroker.publish('setDisplayStyle', {
        style, selectorFn, atoms, globalTarget,
    });
}

function captureDisplayStyle(style, selection) {
    const { atoms, globalTarget } = displayStyleTarget(style, selection);
    if (globalTarget) return captureAppearance({ global: true });
    const scope = mutationScope(atoms);
    return captureAppearance({ groups: scope.groups, atoms: scope.atoms });
}

/** Validate a SetDisplayStyle call and work out what it styles. */
function displayStyleTarget(style, selection) {
    if (selection !== undefined) {
        const { selectorFn, atoms } = resolveAppearanceTarget(selection);
        validateAppearanceStyle(style, atoms, { explicitSelection: true });
        return { selectorFn, atoms, globalTarget: style === MolStyles.surface };
    }
    validateAppearanceStyle(style);
    const selectedAtoms = App.Workspace.getSelectedAtoms();
    if (selectedAtoms.length === 0) return { globalTarget: true };
    return { atoms: selectedAtoms, globalTarget: style === MolStyles.surface };
}

function applySelectionColor(atoms, color, colorAllAtoms) {
    const {
        primaryGroups, subsumedGroups, extraAtomsMap,
    } = partitionAppearanceAtoms(atoms);

    for (const group of primaryGroups) {
        if (color === DefaultColorCode) {
            StyleManager.removeCustomAtomGroupStyle(group, 'color');
            StyleManager.removeCustomAtomGroupStyle(group, 'colorAllAtoms');
        } else {
            StyleManager.setCustomAtomGroupStyle(group, { color, colorAllAtoms });
        }
        StyleManager.removeCustomAtomsColor(group.getAtoms());
    }
    for (const group of subsumedGroups) {
        StyleManager.removeCustomAtomGroupStyle(group, 'color');
        StyleManager.removeCustomAtomGroupStyle(group, 'colorAllAtoms');
    }

    const extraAtoms = [...extraAtomsMap.values()].flat();
    if (color === DefaultColorCode) {
        StyleManager.removeCustomAtomsColor(extraAtoms);
    } else {
        for (const atom of extraAtoms) {
            StyleManager.setCustomAtomsColor(
                [atom], colorAllAtoms || isCarbon(atom) ? color : DefaultColorCode
            );
        }
    }
}

/** SetSelectionColor(selection, colorName, { colorAllAtoms }) -- colorName is a ColorChain key. */
function setSelectionColor(selection, colorName, { colorAllAtoms=false }={}) {
    const atoms = selectionColorTarget(selection, colorName, colorAllAtoms);
    applySelectionColor(atoms, ColorChain[colorName], colorAllAtoms);
    requestAppearanceRefresh();
}

function captureSelectionColor(selection, colorName, { colorAllAtoms=false }={}) {
    const scope = mutationScope(selectionColorTarget(selection, colorName, colorAllAtoms));
    return captureAppearance({ groups: scope.groups, atoms: scope.atoms });
}

/** Validate a SetSelectionColor call and return the atoms it colors. */
function selectionColorTarget(selection, colorName, colorAllAtoms) {
    if (typeof colorName !== 'string'
        || !Object.prototype.hasOwnProperty.call(ColorChain, colorName)) {
        throw new TypeError(`Unsupported molecular color '${colorName}'.`);
    }
    if (typeof colorAllAtoms !== 'boolean') {
        throw new TypeError('colorAllAtoms must be a boolean.');
    }
    return resolveAppearanceTarget(selection).atoms;
}

function completeLabelGroups(atoms, { includeSubsumed=false }={}) {
    const {
        primaryGroups, subsumedGroups, extraAtomsMap,
    } = partitionAppearanceAtoms(atoms);
    const hasExtraAtoms = [...extraAtomsMap.values()]
        .some((extraAtoms) => extraAtoms.length > 0);
    if (primaryGroups.length === 0 || hasExtraAtoms) {
        throw new TypeError('Atom labels require a selection of complete supported atom groups.');
    }
    return includeSubsumed
        ? unique([...primaryGroups, ...subsumedGroups])
        : unique(primaryGroups);
}

/** SetAtomLabel(selection, text) -- labels the complete groups the selection covers. */
function setAtomLabel(selection, text) {
    for (const group of atomLabelGroups(selection, text)) {
        StyleManager.setCustomAtomGroupStyle(group, { label: text, showLabel: true });
    }
    requestAppearanceRefresh();
}

function captureAtomLabel(selection, text) {
    return captureAppearance({ groups: atomLabelGroups(selection, text) });
}

/** Validate a SetAtomLabel call and return the groups it labels. */
function atomLabelGroups(selection, text) {
    if (typeof text !== 'string' || !text.trim()) {
        throw new TypeError('Atom label text must be a non-empty string.');
    }
    return completeLabelGroups(resolveAppearanceTarget(selection).atoms);
}

/** ClearAtomLabels(selection) -- clears the covered groups and any groups they subsume. */
function captureClearAtomLabels(selection) {
    return captureAppearance({ groups: clearLabelGroups(selection) });
}

/** The groups a ClearAtomLabels call clears. */
function clearLabelGroups(selection) {
    const { atoms } = resolveAppearanceTarget(selection);
    return completeLabelGroups(atoms, { includeSubsumed: true });
}

function clearAtomLabels(selection) {
    clearLabelGroups(selection).forEach((group) => {
        StyleManager.removeCustomAtomGroupStyle(group, 'label');
        StyleManager.removeCustomAtomGroupStyle(group, 'showLabel');
        StyleManager.removeCustomAtomGroupStyle(group, 'labelSpec');
    });
    requestAppearanceRefresh();
}

function validatedGlobalStyleState(state) {
    if (!state || typeof state !== 'object' || Array.isArray(state)) {
        throw new TypeError('Global style state must be an object.');
    }
    const allowedKeys = new Set([
        'ProteinViewProteinStyle', 'LigandViewProteinStyle', 'LabelStyle',
    ]);
    const keys = Object.keys(state);
    if (keys.length === 0) throw new TypeError('Global style state cannot be empty.');
    const unknown = keys.find((key) => !allowedKeys.has(key));
    if (unknown) throw new TypeError(`Unknown global style key '${unknown}'.`);

    const normalized = { ...state };
    for (const key of ['ProteinViewProteinStyle', 'LigandViewProteinStyle']) {
        if (key in normalized
            && (typeof normalized[key] !== 'string'
                || !SelectionAppearanceStyles.includes(normalized[key]))) {
            throw new TypeError(`Invalid molecular style for ${key}.`);
        }
    }
    if ('LabelStyle' in normalized) {
        const label = normalized.LabelStyle;
        if (!label || typeof label !== 'object' || Array.isArray(label)) {
            throw new TypeError('LabelStyle must be an object.');
        }
        const labelKeys = Object.keys(label);
        const unknownLabelKey = labelKeys.find((key) => !['fontSize', 'type'].includes(key));
        if (labelKeys.length === 0 || unknownLabelKey) {
            throw new TypeError(
                unknownLabelKey
                    ? `Unknown global label style key '${unknownLabelKey}'.`
                    : 'LabelStyle cannot be empty.'
            );
        }
        const mergedLabel = { ...StyleManager.LabelStyle, ...label };
        if (!Number.isFinite(mergedLabel.fontSize)
            || mergedLabel.fontSize < 6 || mergedLabel.fontSize > 48) {
            throw new TypeError('LabelStyle.fontSize must be a finite number from 6 to 48.');
        }
        if (!['background', 'print'].includes(mergedLabel.type)) {
            throw new TypeError("LabelStyle.type must be 'background' or 'print'.");
        }
        normalized.LabelStyle = mergedLabel;
    }
    return normalized;
}

/**
 * SetGlobalStyleState(state, { refresh }) -- a validated patch of the protein-view, ligand-view
 * and label defaults.
 * @param {{
 *  ProteinViewProteinStyle?: MolStyles[keyof MolStyles]
 *  LigandViewProteinStyle?: MolStyles[keyof MolStyles]
 *  LabelStyle?: import('BMapsSrc/style_manager').labelSpec
 * }} state
 */
function setGlobalStyleState(state, { refresh=true }={}) {
    StyleManager.setGlobalStyleState(validatedGlobalStyleState(state));
    if (refresh) requestAppearanceRefresh();
}

function captureGlobalStyleState() {
    return captureAppearance({ global: true });
}

// ─── Display state commands ───────────────────────────────────────────────────────────────
// Registered with capture / restore pairs built by the helpers below, one per kind of state.

/**
 * Undo for a command that sets display-state `keys`: capture records them, restore puts them back.
 *
 * Read from saveState(), not the DisplayState getters: those fold in temporary overrides (weak
 * H-bonds while a key is held, say), which are not the user's setting and must not become it on
 * undo.
 *
 * The live state only. Publication mode's saved view, which some of these setters also write, is
 * never touched: undo leaves it to the named-views design (docs/gibbs-migration-register.md,
 * "Undo for named views").
 */
function displayStateUndo(...keys) {
    return {
        capture: () => {
            const current = App.Workspace.displayStateController.displayState.saveState();
            return Object.fromEntries(keys.map((key) => [key, current[key]]));
        },
        restore: (state) => App.Workspace.displayStateController.displayState.update(state),
    };
}

/** Undo for the commands that change the active compound: make the previous one active again. */
function activeCompoundUndo() {
    return {
        capture: () => App.Workspace.getActiveCompound(),
        restore: (compound) => {
            if (compound && !App.Workspace.getLoadedCompounds().includes(compound)) {
                Log.warn(`Cannot undo: ${compound.resSpec} has been removed since it was active.`);
                return;
            }
            App.Workspace.activateCompound(compound);
        },
    };
}

/**
 * Undo for a tree toggle: capture each target's previous value of `prop` and restore exactly
 * that, so a call with an explicit forceValue (Hide all, Collapse all) is undone as well as a
 * plain toggle -- re-running the toggle would only undo the plain one.
 *
 * Restore publishes the toggle's own default event, not the caller's. No caller of these commands
 * passes one today; one that passed null would be publishing a combined event itself afterwards,
 * which nothing does on undo.
 */
function toggleUndo(prop, toggle) {
    return {
        capture: (component) => toggleTargets(component).map((item) => ({
            item, forceValue: Boolean(App.Workspace.getAtomGroupState(item, prop)),
        })),
        restore: (previous) => toggle(previous),
    };
}

/**
 * The items a toggle call changes, read the way Workspace.toggleProp reads its argument.
 * @param {Array|Object} component - The component or array of components to toggle.
 *
 * Workspace.toggleProp allows passing a component and forceValue into the function parameters,
 * or as an array of { item, forceValue } objects in the component parameter.
 * This function processes the input in the same way, to always return just the actual components.
 */
function toggleTargets(component) {
    if (!Array.isArray(component)) return [component];
    return component.map((entry) => (
        entry.item && entry.forceValue !== undefined ? entry.item : entry
    ));
}

function setHydrogenView(hydrogens) {
    App.Workspace.displayStateController.setHydrogens(hydrogens);
}

function setWaterView(waters) {
    App.Workspace.displayStateController.setWaters(waters);
}

function setHBonds(hbondMode) {
    App.Workspace.displayStateController.setHBonds(hbondMode);
}

function setPiPiStacking(show) {
    App.Workspace.displayStateController.setPiPiStacking(show);
}

function setDipoles(dipoleMode) {
    App.Workspace.displayStateController.setDipoles(dipoleMode);
}

function setBindingSiteSurface(surfaceMode) {
    App.Workspace.displayStateController.setBindingSiteSurface(surfaceMode);
}

function setLigandSolvation(show) {
    App.Workspace.displayStateController.setLigandSolvation(show);
}

function setHotspots(show) {
    App.Workspace.displayStateController.setHotspots(show);
}

function showBfactor(show) {
    App.Workspace.displayStateController.setBfactor(show);
}

function setFragmentEnergyFilter(fragmentInfo, value) {
    App.Workspace.updateAtomGroupState(fragmentInfo, { energyFilterValue: value });
}

function setColorScheme(color) {
    updateColorScheme(color);
}

function activateCompound(compound) {
    App.Workspace.activateCompound(compound);
}

function activateNextCompound(step) {
    App.Workspace.activateNextCompound(step);
}

function toggleVisibility(component, forceValue, evtName) {
    App.Workspace.toggleVisibility(component, forceValue, evtName);
}

function toggleStarred(component, forceValue, evtName) {
    App.Workspace.toggleStarred(component, forceValue, evtName);
}

function toggleSelection(component, forceValue, evtName) {
    App.Workspace.toggleSelection(component, forceValue, evtName);
}

function toggleExpansion(groupItem, forceValue, evtName) {
    App.Workspace.toggleExpansion(groupItem, forceValue, evtName);
}

/**
 * @param {string} treeName
 * @param {TreeItem} movingItem
 * @param {{
 *     fromIndexPath?: number[],
 *     toIndexPath?: number[],
 *     addAtItem?: TreeItem,
 *     addAfterItem?: TreeItem,
 *     addInItem?: TreeItem,
 * }} pathOptions
 */
function moveTreeItem(treeName, movingItem, pathOptions) {
    App.Workspace.moveTreeItem(treeName, movingItem, pathOptions);
}

/**
 * @param {string} groupName
 * @param {string} treeName
 * @param {{
 *     toItem?: TreeItem
 *     toIndexPath?: number[],
 * }} pathOptions
 */
function groupCreate(groupName, treeName, pathOptions) {
    return App.Workspace.groupCreate(groupName, treeName, pathOptions);
}

/**
 * Put items into a new group in one of the side-bar trees.
 * @param {{
 *     items: TreeItem|*|Array<TreeItem|*>,
 *     treeName: string,
 *     path?: number[],
 *     groupName?: string,
 * }} options
 *   - items: the tree's own rows (as the Selector passes its selection), or the things the tree
 *     lists (compounds, say), whose rows are looked up
 *   - path: where the group goes; by default, where the first item was
 *   - groupName: without one the group is "Grouped Items"
 * @returns {TreeGroup}
 */
function groupItems({
    items, treeName, path, groupName,
}={}) {
    const all = ensureArray(items);
    const rows = all.filter((item) => item instanceof TreeItem);
    const others = all.filter((item) => !(item instanceof TreeItem));
    if (others.length > 0) {
        const found = App.Workspace.getTreeItemsForItems(treeName, others);
        if (found.length === 0) {
            throw new Error(`None of these items is in the ${treeName} tree.`);
        }
        rows.push(...found);
    }
    return App.Workspace.groupItems(rows, treeName, path, groupName ? { groupName } : undefined);
}

function groupRename(group, newName) {
    App.Workspace.groupRename(group, newName);
}

function groupUngroup(group, treeName) {
    App.Workspace.groupUngroup(group, treeName);
}

/**
 * @param {{ treeItem, indexPath }|[{ treeItem, indexPath }]} groupOrGroups
 * @param {string} treeName
 */
function groupDelete(groupOrGroups, treeName) {
    App.Workspace.groupDelete(groupOrGroups, treeName);
}

/**
 * groupSort() - Sort a group in the tree control
 * @param {TreeGroup} group
 * @param {{ sortType: "Alphabetical" | "EnergyScore" }} sortOptions
 */
function groupSort(group, sortOptions) {
    App.Workspace.groupSort(group, sortOptions);
}

/**
 * Sort the protein or the fragment tree, as their menus do. Any other tree, or a sort that is not
 * one the tree's menu offers, is refused with a message: Workspace and DisplayState would ignore
 * it silently.
 * @param {{ treeName: 'Protein' | 'Fragments', sortType: string }} options
 *   - sortType: one of the tree's TreeMenuSorts
 */
function changeTreeSort({ treeName, sortType }={}) {
    const sorts = TreeMenuSorts[treeName];
    if (!sorts) {
        throw new Error(`Only the Protein and Fragments trees can be sorted, not "${treeName}".`);
    }
    if (!sorts.includes(sortType)) {
        throw new Error(`The ${treeName} tree can be sorted by ${sorts.join(', ')}, not "${sortType}".`);
    }
    App.Workspace.changeTreeSort(treeName, sortType);
}

function changeHotspotThreshold(threshold) {
    App.Workspace.changeHotspotThreshold(threshold);
}

/**
 * SetCustomAtomGroupStyle(atomGroup, style) -- the right-click menu's raw setter. It does not
 * repaint; its UI caller owns that. The caller's style object is copied, not written to.
 * A group that cannot be styled (see StyleableAtomGroupTypes) is left alone rather than given a
 * style its drawing never reads -- which also lets an older saved session that stored one restore
 * without error.
 * @param {AtomGroup} atomGroup
 * @param {AtomGroupStyle} style
 */
function setCustomAtomGroupStyle(atomGroup, style={}) {
    if (!atomGroup || !style || typeof style !== 'object' || Array.isArray(style)) {
        throw new TypeError('SetCustomAtomGroupStyle requires an atom group and style object.');
    }
    if (!canStyleAtomGroup(atomGroup)) return;
    const normalized = { ...style };
    if (normalized.molStyle) {
        // If it's the default style, change to 'default' so it will be cleared instead of stored
        const defaultStyle = App.Workspace.getDefaultAtomGroupStyle(atomGroup);
        if (normalized.molStyle === defaultStyle) normalized.molStyle = DefaultMolStyleCode;
    }
    StyleManager.setCustomAtomGroupStyle(atomGroup, normalized);
}

/**
 * Redo for SetCustomAtomGroupStyle. The setter leaves repainting to its UI caller, but a redo
 * runs the command alone, without that caller -- so the style lands in StyleManager and nothing
 * is drawn until some other command happens to repaint. Goes through the composed doFn rather
 * than the raw setter, so the redo re-captures and a later undo still has a snapshot.
 *
 * By the time the repaint runs the redo has happened, so a repaint that throws is logged rather
 * than rethrown: UserAction.redo() reads any throw as "the redo did not happen" and steps back,
 * which would leave an applied change still offered for redo and no longer undoable.
 */
function redoCustomAtomGroupStyle(...args) {
    DisplayCmds.SetCustomAtomGroupStyle.doFn(...args);
    try {
        requestAppearanceRefresh();
    } catch (error) {
        Log.error(`SetCustomAtomGroupStyle: redo applied, but its repaint failed: ${error}`);
    }
}

function captureCustomAtomGroupStyle(atomGroup) {
    return captureAppearance({ groups: canStyleAtomGroup(atomGroup) ? [atomGroup] : [] });
}

/**
 * @param {AtomGroup} atomGroup
 * @param {keyof AtomGroupStyle} styleType
 * styleType is an optional argument. If you want to remove all styles just pass in atomGroup
 */
function removeCustomAtomGroupStyle(atomGroup, styleType) {
    StyleManager.removeCustomAtomGroupStyle(atomGroup, styleType);
}

/**
 * @param {Atom[]} atoms
 * @param {MolStyles[keyof MolStyles]} style
 */
function setCustomAtomsStyle(atoms, style) {
    StyleManager.setCustomAtomsStyle(atoms, style);
}

/** @param {Atom[]} atoms */
function removeCustomAtomsStyle(atoms) {
    StyleManager.removeCustomAtomsStyle(atoms);
}

/**
 * Redraw existing 3D atoms.
 */
function redisplay() {
    EventBroker.publish('redisplayRequest');
}

/**
 * Redraw the whole scene according to current settings.
 * @param {{ forceProteinView: boolean, recenter: boolean }} options
 */
function redrawScene(options) {
    EventBroker.publish('redrawSceneRequest', options);
}
